import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Level5Item } from "./Level5Item";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Level4ItemProps = {
  item: {
    id: number;
    name: string;
    index: string;
    title?: string;
    description?: string;
    node_1?: string;
    node_2?: string;
    node_3?: string;
    node_4?: string;
    product_count?: string;
    url?: string;
    cat_text?: string;
  };
  isExpanded: boolean;
  onToggle: () => void;
  selectedUpdate: string;
};

export const Level4Item = ({ item, isExpanded, onToggle, selectedUpdate }: Level4ItemProps) => {
  const { data: level5Categories } = useQuery({
    queryKey: ["level5-categories", item.name, isExpanded, selectedUpdate],
    enabled: isExpanded,
    queryFn: async () => {
      let query = supabase
        .from("categories_atp_autoteile_de")
        .select("*")
        .eq("level", 5)
        .eq("parent_node_name", item.name);

      if (selectedUpdate && selectedUpdate !== "all") {
        const [hour, date] = selectedUpdate.split('h ');
        const [day, month, year] = date.split('-');
        const startDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour)
        );
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        query = query
          .gte('created_at', startDate.toISOString())
          .lt('created_at', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  // Check if there are any child categories
  const { data: hasChildren } = useQuery({
    queryKey: ["has-level5-categories", item.name],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("categories_atp_autoteile_de")
        .select("*", { count: 'exact', head: true })
        .eq("level", 5)
        .eq("parent_node_name", item.name);

      if (error) throw error;
      return count && count > 0;
    },
  });

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
          <div className="w-5">
            {hasChildren && (
              <ChevronRight
                className={`h-5 w-5 transition-transform ${
                  isExpanded ? "rotate-90" : ""
                }`}
              />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="font-medium">
                  {item.url ? (
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.name}
                    </a>
                  ) : (
                    item.name
                  )}
                </div>
                <Badge 
                  variant={item.index === "index, follow" ? "default" : "destructive"}
                >
                  {item.index}
                </Badge>
                <Badge
                  variant={item.cat_text ? "default" : "secondary"}
                  className={item.cat_text ? "bg-green-500" : "bg-red-200"}
                >
                  {item.cat_text ? "Found content" : "no content"}
                </Badge>
              </div>
              {item.product_count && (
                <div className="text-sm text-muted-foreground">
                  Products: {item.product_count}
                </div>
              )}
            </div>
            {item.title && (
              <p className="text-sm text-muted-foreground text-left font-medium">
                {item.title}
              </p>
            )}
            {item.description && (
              <p className="text-sm text-muted-foreground text-left">
                {item.description}
              </p>
            )}
            <div className="text-sm text-muted-foreground">
              {(item.node_1 || item.node_2 || item.node_3 || item.node_4) && (
                <Breadcrumb>
                  <BreadcrumbList>
                    {item.node_1 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{item.node_1}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {(item.node_2 || item.node_3 || item.node_4) && <BreadcrumbSeparator />}
                      </>
                    )}
                    {item.node_2 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{item.node_2}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {(item.node_3 || item.node_4) && <BreadcrumbSeparator />}
                      </>
                    )}
                    {item.node_3 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{item.node_3}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {item.node_4 && <BreadcrumbSeparator />}
                      </>
                    )}
                    {item.node_4 && (
                      <BreadcrumbItem>
                        <BreadcrumbLink href="#">{item.node_4}</BreadcrumbLink>
                      </BreadcrumbItem>
                    )}
                  </BreadcrumbList>
                </Breadcrumb>
              )}
            </div>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-8 space-y-2 mt-2">
          {level5Categories?.map((level5Item) => (
            <Level5Item
              key={level5Item.id}
              item={level5Item}
              isExpanded={false}
              onToggle={() => {}}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};